import { useCallback, useMemo } from 'react';
import { ROLE_TYPES } from '@/Interfaces/Data';
import { useCities } from '@/_hooks/use-cities';
import { useAppSelector } from '@/_store/hooks';
import { selectUser } from '@/_store/modules/auth/selectors';
import { normalizeMunicipioIds } from '@/_utils/normalize-municipio-ids';

function useLinkedMunicipioIds() {
  const user = useAppSelector(selectUser);
  const { cities, loadingCities, CITIES_OPTIONS } = useCities();

  const isLogisticOperator =
    user?.managerLogType === ROLE_TYPES.LOGISTIC_OPERATOR;

  const sessionMunicipioIds = useMemo(
    () => normalizeMunicipioIds(user?.municipioIds),
    [user?.municipioIds],
  );

  const linkedMunicipioIds = useMemo(() => {
    if (!isLogisticOperator) {
      return null;
    }

    if (sessionMunicipioIds.length > 0) {
      return sessionMunicipioIds;
    }

    return (cities || []).map(city => city._id);
  }, [cities, isLogisticOperator, sessionMunicipioIds]);

  const scopedCityOptions = useMemo(() => {
    if (!linkedMunicipioIds) {
      return CITIES_OPTIONS;
    }

    return CITIES_OPTIONS.filter(option =>
      linkedMunicipioIds.includes(String(option.value)),
    );
  }, [CITIES_OPTIONS, linkedMunicipioIds]);

  const isMunicipioAllowed = useCallback(
    (municipioId?: string) => {
      if (!linkedMunicipioIds) {
        return true;
      }

      if (!municipioId) {
        return false;
      }

      return linkedMunicipioIds.includes(municipioId);
    },
    [linkedMunicipioIds],
  );

  const isCheckingLinkedMunicipios =
    isLogisticOperator && sessionMunicipioIds.length === 0 && loadingCities;

  return {
    isLogisticOperator,
    linkedMunicipioIds,
    scopedCityOptions,
    isMunicipioAllowed,
    isCheckingLinkedMunicipios,
    loadingCities,
  };
}

export { useLinkedMunicipioIds };
